import React from 'react';
import { SlideMode } from './types';

const BINDING = /\{\{\s*([a-z_][a-z0-9_.]*)\s*(?:\|([^}]*))?\}\}/gi;

// #region fill
/**
 * Fills `{{key}}` and `{{key|fallback}}` from the context values. A known key with an empty value uses its fallback
 * (or nothing). Unknown keys are left as typed in edit mode (so the rep sees the typo) and removed in view and print.
 */
export function fill(text: string | null | undefined, values: Record<string, string>, mode: SlideMode = 'view'): string {
    if (!text) return '';
    return text.replace(BINDING, (whole: string, key: string, fallback?: string) => {
        const known = Object.prototype.hasOwnProperty.call(values, key);
        if (!known) return mode === 'edit' ? whole : (fallback ?? '').trim();
        const v = values[key];
        return v && v.trim() ? v : (fallback ?? '').trim();
    });
}

// #region bindingParts
export type TextPart = { kind: 'text'; text: string } | { kind: 'binding'; text: string; key: string; known: boolean };

/** Splits raw text into plain runs and `{{binding}}` runs, for the edit-mode chips. */
export function bindingParts(text: string, values: Record<string, string>): TextPart[] {
    const parts: TextPart[] = [];
    let last = 0;
    for (const m of Array.from(text.matchAll(BINDING))) {
        const at = m.index ?? 0;
        if (at > last) parts.push({ kind: 'text', text: text.slice(last, at) });
        parts.push({ kind: 'binding', text: m[0], key: m[1], known: Object.prototype.hasOwnProperty.call(values, m[1]) });
        last = at + m[0].length;
    }
    if (last < text.length) parts.push({ kind: 'text', text: text.slice(last) });
    return parts;
}

// #region inlineMarks
const MARK = /\*\*([^*\n]+?)\*\*|\*([^*\n]+?)\*/g;

/** `**bold**` and `*italic*` to React nodes. Everything else stays plain text (never HTML). */
export function inlineMarks(text: string): React.ReactNode {
    if (!text.includes('*')) return text;
    const out: React.ReactNode[] = [];
    let last = 0;
    let n = 0;
    for (const m of Array.from(text.matchAll(MARK))) {
        const at = m.index ?? 0;
        if (at > last) out.push(text.slice(last, at));
        out.push(m[1] !== undefined ? <strong key={n++}>{m[1]}</strong> : <em key={n++}>{m[2]}</em>);
        last = at + m[0].length;
    }
    if (last < text.length) out.push(text.slice(last));
    return out;
}

// #region longtextBlocks
export type TextBlock = { kind: 'p'; text: string } | { kind: 'ul'; items: string[] };

/** A longtext field: blank line = new paragraph, lines starting "- " = bullets. */
export function longtextBlocks(text: string): TextBlock[] {
    const blocks: TextBlock[] = [];
    let para: string[] = [];
    let list: string[] = [];
    const flushPara = (): void => { if (para.length) blocks.push({ kind: 'p', text: para.join('\n') }); para = []; };
    const flushList = (): void => { if (list.length) blocks.push({ kind: 'ul', items: list }); list = []; };
    for (const raw of text.replace(/\r\n/g, '\n').split('\n')) {
        const line = raw.trimEnd();
        if (/^\s*[-•]\s+/.test(line)) { flushPara(); list.push(line.replace(/^\s*[-•]\s+/, '')); continue; }
        if (!line.trim()) { flushPara(); flushList(); continue; }
        flushList();
        para.push(line);
    }
    flushPara();
    flushList();
    return blocks;
}

// #region sizeStep
/**
 * Picks a font size from the text's length: long copy steps down a size or two so it never overflows.
 * `steps` is [[maxChars, px], ...] in ascending maxChars; the last entry covers anything longer.
 */
export function sizeStep(text: string, steps: [number, number][]): number {
    const len = text.length;
    for (const [max, px] of steps) if (len <= max) return px;
    return steps[steps.length - 1][1];
}

// #region formatDate
/** "2026-12-04" → "December 4, 2026"; anything unparseable comes back unchanged. */
export function formatDate(iso: string): string {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
    if (!m) return iso;
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

// #region money
export function money(n: number, currency = 'USD', cents = false): string {
    try {
        return n.toLocaleString('en-US', { style: 'currency', currency, minimumFractionDigits: cents ? 2 : 0, maximumFractionDigits: cents ? 2 : 0 });
    } catch {
        return `$${n.toFixed(cents ? 2 : 0)}`;
    }
}
